/**
 * What each weather variable is called at Open-Meteo, and what colour it draws.
 *
 * This is the file that un-greys the picker. Rain, gusts and pressure were never
 * hard, they were just not arrows: each one is a single number per grid point, and
 * once a number per point can be washed across the chart (fieldRaster.ts) the only
 * thing that separates them is the name of the field in the request and the colours
 * on the ramp. So both of those live here, one entry per variable, and adding air
 * temperature or visibility later is a few lines rather than a new layer.
 *
 * ONE VARIABLE PER REQUEST, deliberately. Open-Meteo rejects the whole request when
 * a model does not carry one of the fields asked for, so bundling gusts in with the
 * wind turned a working model into a blank chart, silently, which is what killed
 * two of the seven models on build 302. Asking for one thing at a time means a
 * model that has no gusts costs us the gusts and nothing else, and we can say so.
 */
import { windColourSteps } from './windArrow';
import { AGREEMENT_THRESHOLDS } from '@/weather/consensus';
import type { RampStop } from './fieldRaster';

/** '#31c17a' to [49, 193, 122]. */
export function hexRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

export interface FieldVariable {
  /** Matches the overlay store's variable id. */
  id: string;
  /** How it is named in a sentence, for the "this model has no ..." message. */
  name: string;
  /** The exact Open-Meteo `hourly=` field name. */
  hourly: string;
  /** For a legend and a readout. */
  unit: string;
  /** How many decimals are worth showing. */
  decimals: number;
  stops: RampStop[];
  /**
   * Below this the field is left clear. Rain is the reason: a blue wash over the
   * whole chart on a dry afternoon tells a skipper nothing and hides the chart
   * underneath it, so no rain draws as no colour.
   */
  hideBelow?: number;
  /** How solid the wash is. Rain sits heavier because it is read as coverage. */
  alpha: number;
  /** True when the variable comes off the marine endpoint (its own models). */
  marine?: boolean;
  /**
   * How far from the middle a model has to be before the blend sets it aside.
   * Per variable, because 5 hPa of pressure and 5 mm of rain are not comparable
   * amounts of disagreement.
   */
  outlierAt: number;
  /** One line for the panel, saying what is actually being shown. */
  note: string;
}

/**
 * The LIVE wind ramp, with the user's thresholds applied (fb-msmaz0fj-3).
 * Recomputed on read so the wash and the legend follow a settings change
 * without anyone having to remember to rebuild them.
 */
function windStopsNow(): RampStop[] {
  return windColourSteps().map((s) => ({ value: s.fromKn, rgb: hexRgb(s.colour) }));
}

/**
 * Pressure diverges about 1013 hPa rather than running cold to hot.
 *
 * A single ramp would put the interesting part, the gradient, in the middle where
 * the colours change least. Diverging from standard pressure puts low to one side
 * and high to the other, so a deep low reads as a distinct colour at a glance and
 * the isobars pack visibly where the wind will be. Standard pressure itself is
 * near-white on purpose, because it is the boring case and should look it.
 */
const PRESSURE_STOPS: RampStop[] = [
  { value: 960, rgb: [90, 30, 120] },
  { value: 980, rgb: [56, 96, 196] },
  { value: 1000, rgb: [130, 190, 230] },
  { value: 1013, rgb: [245, 245, 238] },
  { value: 1025, rgb: [240, 190, 90] },
  { value: 1040, rgb: [214, 78, 40] },
];

/**
 * Significant wave height, metres. The boundaries are decision points rather
 * than an even spread: half a metre is where a small boat starts to notice, two
 * is where most start planning around it, and four is a day with its own plans.
 */
const WAVE_STOPS: RampStop[] = [
  { value: 0, rgb: [200, 228, 240] },
  { value: 0.5, rgb: [130, 190, 225] },
  { value: 1, rgb: [70, 145, 205] },
  { value: 2, rgb: [45, 95, 180] },
  { value: 3, rgb: [110, 70, 175] },
  { value: 5, rgb: [170, 45, 130] },
  { value: 8, rgb: [200, 30, 60] },
];

/** Rain in mm per hour. The steps are the ones a forecast actually distinguishes. */
const RAIN_STOPS: RampStop[] = [
  { value: 0.1, rgb: [178, 226, 226] },
  { value: 0.5, rgb: [102, 194, 214] },
  { value: 1, rgb: [58, 140, 201] },
  { value: 2, rgb: [45, 95, 180] },
  { value: 4, rgb: [110, 70, 175] },
  { value: 8, rgb: [160, 45, 140] },
  { value: 16, rgb: [190, 30, 70] },
];

export const FIELD_VARIABLES: FieldVariable[] = [
  {
    id: 'wind',
    name: 'wind',
    hourly: 'wind_speed_10m',
    unit: 'kn',
    decimals: 0,
    // A getter, so the ramp tracks the user's thresholds live.
    get stops() {
      return windStopsNow();
    },
    // 150 drowned the chart in the field (fb-msie7xvq-1: "I can't see the chart
    // underneath"): a 15 kn day washes the whole screen green, land included.
    // 100 keeps the gradient readable while the chart stays a chart.
    alpha: 100,
    outlierAt: AGREEMENT_THRESHOLDS.windKn.outlierAt,
    note: 'Wind at 10 m, on the Beaufort bands. Arrows point the way it is blowing.',
  },
  {
    id: 'gust',
    name: 'gusts',
    hourly: 'wind_gusts_10m',
    unit: 'kn',
    decimals: 0,
    get stops() {
      return windStopsNow();
    },
    // Same weight as the wind, and lightened with it: the two are compared at a
    // glance, so they must sit at the same opacity.
    alpha: 100,
    outlierAt: AGREEMENT_THRESHOLDS.gustKn.outlierAt,
    // Same ramp as the wind on purpose: the useful question about a gust is how it
    // compares with the mean wind, and two different colour scales would make that
    // comparison a sum instead of a glance.
    note: 'The strongest gust forecast in the hour, on the same colour scale as the wind.',
  },
  {
    id: 'rain',
    name: 'rainfall',
    hourly: 'precipitation',
    unit: 'mm/h',
    decimals: 1,
    stops: RAIN_STOPS,
    hideBelow: 0.1,
    alpha: 175,
    outlierAt: AGREEMENT_THRESHOLDS.rainMm.outlierAt,
    note: 'Rainfall in the hour. Dry ground is left clear so the chart stays readable.',
  },
  {
    id: 'pressure',
    name: 'pressure',
    hourly: 'pressure_msl',
    unit: 'hPa',
    decimals: 0,
    stops: PRESSURE_STOPS,
    alpha: 130,
    outlierAt: AGREEMENT_THRESHOLDS.pressureHpa.outlierAt,
    note: 'Sea-level pressure. Where the colour changes fastest is where the wind is.',
  },
  {
    id: 'waves',
    name: 'wave height',
    hourly: 'wave_height',
    unit: 'm',
    decimals: 1,
    stops: WAVE_STOPS,
    alpha: 160,
    marine: true,
    outlierAt: AGREEMENT_THRESHOLDS.waveM.outlierAt,
    note: 'Significant wave height, from the wave models. Wind arrows stay on top: sea and wind are read together.',
  },
];

export function fieldVariable(id: string): FieldVariable | null {
  return FIELD_VARIABLES.find((v) => v.id === id) ?? null;
}

/** The legend entries for a variable, low to high, for the panel's colour key. */
export function legendFor(v: FieldVariable): { label: string; colour: string }[] {
  return v.stops.map((s) => ({
    label: `${s.value.toFixed(v.decimals)}`,
    colour: `rgb(${s.rgb[0]}, ${s.rgb[1]}, ${s.rgb[2]})`,
  }));
}
